import { useMutation, useQuery } from '@tanstack/react-query';

import { selectActiveAccount, useAuthStore } from '@features/auth';
import { queryClient } from '@lib/queryClient';
import type { ApiError } from '@/types/api.types';

import { createTreatment, getTreatments, updateTreatment } from '../api/specialtiesApi';
import type {
  CreateTreatmentRequest,
  Treatment,
  TreatmentListData,
  UpdateTreatmentRequest,
} from '../types';

export const treatmentsKey = (accountId: string, specialtyId: string) =>
  ['treatments', accountId, specialtyId] as const;

type UpdateVariables = { treatmentId: string; body: UpdateTreatmentRequest };

export function useTreatments(specialtyId: string) {
  const activeAccount = useAuthStore(selectActiveAccount);
  const accountId = activeAccount?.accountId ?? '';

  const query = useQuery({
    queryKey: treatmentsKey(accountId, specialtyId),
    queryFn: () => getTreatments(accountId, specialtyId),
    enabled: !!accountId && !!specialtyId,
  });

  const createMutation = useMutation<Treatment, ApiError, CreateTreatmentRequest>({
    mutationFn: (body) => createTreatment(accountId, specialtyId, body),
    onSuccess: (created) => {
      queryClient.setQueryData<TreatmentListData>(
        treatmentsKey(accountId, specialtyId),
        (prev) =>
          prev
            ? { treatments: [...prev.treatments, created], count: prev.count + 1 }
            : { treatments: [created], count: 1 },
      );
    },
  });

  const updateMutation = useMutation<Treatment, ApiError, UpdateVariables>({
    mutationFn: ({ treatmentId, body }) =>
      updateTreatment(accountId, specialtyId, treatmentId, body),
    onSuccess: (updated) => {
      queryClient.setQueryData<TreatmentListData>(
        treatmentsKey(accountId, specialtyId),
        (prev) =>
          prev
            ? {
                treatments: prev.treatments.map((t) => (t.id === updated.id ? updated : t)),
                count: prev.count,
              }
            : { treatments: [updated], count: 1 },
      );
    },
  });

  return {
    treatments: query.data?.treatments ?? [],
    isLoading: query.isLoading,
    fetchError: query.error as ApiError | null,

    create: createMutation.mutate,
    isCreating: createMutation.isPending,
    createError: createMutation.error,

    update: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
    updatingTreatmentId: updateMutation.isPending
      ? (updateMutation.variables?.treatmentId ?? null)
      : null,
  };
}
